/**
 * Core
 */
import Vue from 'vue';
import Router from 'vue-router';
import RedirectUtils from './utils.js';
import AppConfig from './../.env';

/**
 * Views
 */
import Splashscreen from './../views/SplashScreen';

// lazy-loaded parts
import Login from './../views/Login';
import Register from './../views/Register';
import Logout from './../views/Logout';
import Dashboard from './../views/Dashboard';
import Customers from './../views/Customers';
import CustomerFormWrapper from './../views/forms/customer/CustomerFormWrapper';
import Customer from './../views/forms/customer/Customer';
import Inquiries from './../views/Inquiries';
import Inquiry from './../views/forms/inquiry/Inquiry';
import CalendarWrapper from './../views/CalendarWrapper';
import Communication from './../views/Communication';
import Messages from './../views/Messages';
import MessageFormWrapper from './../views/forms/message/MessageFormWrapper';
import Settlements from './../views/Settlements';
import Settings from './../views/Settings';
import Articles from './../views/Articles';
import ArticleForm from './../views/forms/articles/ArticleForm';
import RegisterMailSent from './../views/RegisterMailSent';
import ForgotPassword from './../views/ForgotPassword';
import PasswordReset from './../views/PasswordReset';
import InvitationConfirm from './../views/InvitationConfirm';
import ActivateAccount from './../views/ActivateAccount';
import Opinions from './../views/Opinions';
import AdvisersFinder from './../views/AdvisersFinder';
import PromotionSlotsList from './../views/PromotionSlotsList';
import PageNotFound from './../views/PageNotFound';

/**
 * Subviews
 */

/**
 * Helpers
 */

// Avoid NavigationDuplicated errors
const originalPush = Router.prototype.push;

Router.prototype.push = function push (location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }

  return originalPush.call(this, location).catch(error => {
    if (error && error.name !== 'NavigationDuplicated') {
      throw error;
    }
  });
};

Vue.use(Router);

const router = new Router({
  mode: AppConfig.isMobileApp ? 'hash' : 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: '/logowanie/:message?',
      component: Login,
      name: 'Logowanie',
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/wyloguj',
      component: Logout,
      name: 'Wyloguj'
    },
    {
      path: '/rejestracja/:token?',
      alias: '/register/:token',
      component: Register,
      name: 'Rejestracja',
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/rejestracja-eksperta',
      alias: '/register-adviser',
      component: Register,
      name: 'Rejestracja Eksperta',
      meta: {
        layout: 'guest',
        isAdviser: true
      }
    },
    {
      path: '/aktywacja-konta/:token?',
      alias: '/activate/:token?',
      component: ActivateAccount,
      name: 'Aktywacja konta',
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/activate-adviser/:token',
      component: ActivateAccount,
      name: 'Aktywacja konta eksperta',
      meta: {
        layout: 'guest',
        isAdviser: true
      }
    },
    {
      path: '/rejestracja-wiadomosc-wyslana',
      component: RegisterMailSent,
      name: 'Rejestracja - Wiadomość wysłana',
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/potwierdzenie-zaproszenia/:token',
      component: InvitationConfirm,
      name: 'Rejestracja - potwierdzenie zaproszenia',
      query: {
        email: 'email'
      },
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/odzyskaj-haslo',
      component: ForgotPassword,
      name: 'Odzyskaj hasło',
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/resetowanie-hasla/:token',
      component: PasswordReset,
      name: 'Reset hasła',
      meta: {
        layout: 'guest'
      }
    },
    {
      path: '/wczytywanie',
      component: Splashscreen,
      name: 'Splashscreen'
    },
    {
      path: '/',
      redirect: '/pulpit'
    },
    {
      path: '/pulpit',
      component: Dashboard,
      name: 'Pulpit',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('dashboard', next);
      }
    },
    {
      path: '/klienci',
      component: Customers,
      name: 'Klienci',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('customers', next);
      }
    },
    {
      path: '/klienci/nowy',
      component: CustomerFormWrapper,
      name: 'Dodawanie klienta',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('customers', next);
      }
    },
    {
      path: '/klienci/:id(\\d+)',
      component: Customer,
      name: 'Profil klienta',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('customers', next);
      }
    },
    {
      path: '/klienci/:id(\\d+)/lista-wiadomosci',
      component: Customer,
      name: 'Profil klienta - lista wiadomości',
      meta: {
        activeTab: 'customer-messages'
      },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('customers', next);
      },
    },
    {
      path: '/klienci/:id(\\d+)/lista-ofert',
      component: Customer,
      name: 'Profil klienta - lista ofert',
      meta: {
        activeTab: 'customer-inquiries'
      },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('customers', next);
      },
    },
    {
      path: '/oferty-kredytowe',
      component: Inquiries,
      name: 'Oferty kredytowe',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      }
    },
    {
      path: '/oferty-kredytowe/nowa',
      component: Inquiry,
      name: 'Dodawanie oferty kredytowej',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      },
      props: true
    },
    {
      path: '/oferty-kredytowe/:id(\\d+)',
      component: Inquiry,
      name: 'Podgląd oferty kredytowej',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      }
    },
    {
      path: '/oferty-kredytowe/:id(\\d+)/lista-wiadomosci',
      component: Inquiry,
      name: 'Podgląd oferty kredytowej - lista wiadomości',
      meta: {
        activeTab: 'inquiry-messages'
      },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      }
    },
    {
      path: '/oferty-kredytowe/:id(\\d+)/reklamacje',
      component: Inquiry,
      name: 'Podgląd oferty kredytowej - reklamacje',
      meta: {
        activeTab: 'inquiry-complaints'
      },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      }
    },
    {
      path: '/oferty-kredytowe/:id(\\d+)/oferty',
      component: Inquiry,
      name: 'Podgląd oferty kredytowej - oferty dla klienta',
      meta: {
        activeTab: 'inquiry-offers'
      },
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      }
    },
    {
      path: '/klienci/:idKlienta(\\d+)/lista-ofert/:id(\\d+)',
      component: Inquiry,
      name: 'Profil klienta - podgląd oferty kredytowej',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      }
    },
    {
      path: '/kalendarz',
      component: CalendarWrapper,
      name: 'Kalendarz',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('calendar', next);
      }
    },
    {
      path: '/komunikacja',
      component: Communication,
      name: 'Masowa komunikacja z klientami',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('communication', next);
      }
    },
    {
      path: '/wiadomosci',
      component: Messages,
      name: 'Wiadomości',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('messages', next);
      }
    },
    {
      path: '/wiadomosci/:id(\\d+)',
      component: MessageFormWrapper,
      name: 'Edycja wiadomości',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('messages', next);
      }
    },
    {
      path: '/klienci/:idKlienta(\\d+)/lista-wiadomosci/:id(\\d+)',
      component: MessageFormWrapper,
      name: 'Profil klienta - edycja wiadomości',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('messages', next);
      }
    },
    {
      path: '/rozliczenia',
      component: Settlements,
      name: 'Rozliczenia',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settlements', next);
      }
    },
    {
      path: '/ustawienia',
      component: Settings,
      name: 'Ustawienia',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('settings', next);
      }
    },
    {
      path: '/artykuly',
      component: Articles,
      name: 'Artykuły',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('articles', next);
      }
    },
    {
      path: '/artykuly/nowy',
      component: ArticleForm,
      name: 'Dodawanie artykułu',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('articles', next);
      }
    },
    {
      path: '/artykuly/:id(\\d+)',
      component: ArticleForm,
      name: 'Edycja artykułu',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('articles', next);
      }
    },
    {
      path: '/promocje',
      component: PromotionSlotsList,
      name: 'Promocje',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('slots', next);
      }
    },
    {
      path: '/opinie',
      component: Opinions,
      name: 'Opinie',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('inquiries', next);
      }
    },
    {
      path: '/eksperci-finansowi',
      component: AdvisersFinder,
      name: 'Eksperci finansowi',
      beforeEnter: (to, from, next) => {
        RedirectUtils.permissionsCheck('advisers', next);
      }
    },
    {
      path: '/not-found',
      component: PageNotFound,
      meta: {
        isLoggedInView: true
      }
    },
    {
      path: '/:catchAll(.*)',
      redirect: '/not-found',
      meta: {
        isLoggedInView: true
      }
    }
  ]
});

router.beforeEach((to, from, next) => {
  let notLoggedInRoutes = [
    'Logowanie',
    'Wyloguj',
    'Rejestracja',
    'Rejestracja Eksperta',
    'Aktywacja konta',
    'Aktywacja konta eksperta',
    'Rejestracja - Wiadomość wysłana',
    'Rejestracja - potwierdzenie zaproszenia',
    'Odzyskaj hasło',
    'Reset hasła'
  ];

  if (notLoggedInRoutes.indexOf(to.name) !== -1) {
    let splashscreenHtml = document.querySelector('#splashscreen');

    if (splashscreenHtml) {
      splashscreenHtml.parentNode.removeChild(splashscreenHtml);
    }

    next();
    return;
  }

  if (!window.crmApp) {
    setTimeout(() => {
      next({
        name: 'Splashscreen',
        params: {
          nextRoute: to.path
        },
        query: {
          email: to.query.email
        }
      });

      let splashscreenHtml = document.querySelector('#splashscreen');

      if (splashscreenHtml) {
        splashscreenHtml.parentNode.removeChild(splashscreenHtml);
      }
    }, 0);

    return;
  }

  next();
});

export default router;
